import React, { useState, useMemo } from 'react';
import { ChevronDown, ChevronUp, BookOpen, Divide } from 'lucide-react';

export const DreamInterpretationResult = ({ interpretation }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // Memoized processing of interpretation
  const processedInterpretation = useMemo(() => {
    // If interpretation is not a string, return empty array
    if (typeof interpretation !== 'string') {
      console.warn('Invalid interpretation type');
      return [];
    }
    return interpretation.split('**');
  }, [interpretation]);

  // Function to generate a preview snippet
  const generatePreview = (text) => {
    if (typeof text !== 'string') {
      return 'Invalid interpretation';
    }
    
    const sections = text.split('**');
    return sections.length > 1 
      ? (sections[1].slice(0, 100) + '...') 
      : (text.slice(0, 100) + '...');
  };

  // Safety check for interpretation
  if (!interpretation) {
    return null;
  }

  return (
    <div className="bg-purple-900/10 border border-purple-500/30 rounded-xl p-4 mt-4 transition-all duration-300">
      <div 
        className="flex justify-between items-center cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center space-x-2">
          <BookOpen className="h-6 w-6 text-purple-500" />
          <div className="text-lg font-semibold text-white">
            Dream Interpretation
          </div>
        </div>
        <button className="text-purple-300 hover:text-purple-100">
          {isExpanded ? (
            <ChevronUp className="h-6 w-6" />
          ) : (
            <ChevronDown className="h-6 w-6" />
          )}
        </button>
      </div>

      {!isExpanded ? (
        <div className="mt-2 text-gray-300 italic">
          {generatePreview(interpretation)}
        </div>
      ) : (
        <div className="mt-4 space-y-4 text-gray-200">
          {processedInterpretation.map((section, index) => {
            if (index % 2 === 1) {
              // Sections between ** are headings
              return (
                <div key={index} className="text-xl font-bold text-purple-300 mt-4">
                  {section}
                </div>
              );
            } else if (section.trim()) {
              // Sections with actual content
              return (
                <div 
                  key={index} 
                  className="bg-purple-900/20 p-4 rounded-lg border border-purple-500/20"
                >
                  {section.split('*').map((item, itemIndex) => {
                    if (item.trim().startsWith('**')) {
                      // Bolded items (like in the list)
                      return (
                        <div key={itemIndex} className="mb-2">
                          <span className="font-bold text-purple-200">
                            {item.replace(/\*/g, '').trim()}
                          </span>
                        </div>
                      );
                    } else if (item.trim()) {
                      // Regular paragraphs or list items
                      return (
                        <div 
                          key={itemIndex} 
                          className="text-gray-300 mb-2 pl-4 border-l-2 border-purple-500"
                        >
                          {item.trim()}
                        </div>
                      );
                    }
                    return null;
                  })}
                </div>
              );
            }
            return null;
          })}
        </div>
      )}
    </div>
  );
};